import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/inventory',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/inventory',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/inventory',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/inventory',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/inventory',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/inventory',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/stores/v3/inventory-items',
        destPath: '/v3/inventory-items',
      },
      {
        srcPath: '/stores/v3/bulk/inventory-items',
        destPath: '/v3/bulk/inventory-items',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/inventory',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/inventory',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_stores_inventory-items-v-3';

/**
 * Creates an inventory item.
 *
 * The combination of `variantId` and `locationId` must be unique.
 */
export function createInventoryItem(
  payload: object
): RequestOptionsFactory<any> {
  function __createInventoryItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'inventoryItem.createdDate' },
          { path: 'inventoryItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.CreateInventoryItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/inventory-items',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'inventoryItem.createdDate' },
              { path: 'inventoryItem.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createInventoryItem;
}

/**
 * Creates multiple inventory items.
 *
 * The combination of `variantId` and `locationId` must be unique for each inventory item.
 */
export function bulkCreateInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateInventoryItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'inventoryItems.createdDate' },
          { path: 'inventoryItems.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkCreateInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateInventoryItems;
}

/** Retrieves an inventory item. */
export function getInventoryItem(payload: object): RequestOptionsFactory<any> {
  function __getInventoryItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'GET' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.GetInventoryItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/inventory-items/{inventoryItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'inventoryItem.createdDate' },
              { path: 'inventoryItem.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getInventoryItem;
}

/**
 * Updates an inventory item.
 *
 *
 * Each time the inventory item is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the inventory item.
 * This ensures you're working with the latest inventory item and prevents unintended overwrites.
 */
export function updateInventoryItem(
  payload: object
): RequestOptionsFactory<any> {
  function __updateInventoryItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'inventoryItem.createdDate' },
          { path: 'inventoryItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'PATCH' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.UpdateInventoryItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/inventory-items/{inventoryItem.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'inventoryItem.createdDate' },
              { path: 'inventoryItem.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateInventoryItem;
}

/**
 * Updates multiple inventory items.
 *
 * Each time an inventory item is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating an inventory item.
 * This ensures you're working with the latest inventory item and prevents unintended overwrites.
 */
export function bulkUpdateInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateInventoryItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'inventoryItems.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'inventoryItems.inventoryItem.createdDate' },
          { path: 'inventoryItems.inventoryItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkUpdateInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateInventoryItems;
}

/**
 * Creates or updates multiple inventory items.
 *
 * The upsert key is the combination of `productId`, `variantId`, and `locationId`.
 * If `locationId` isn't provided, the site's default location is used.
 *
 * For newly created items, `field_mask` is ignored and all provided fields are applied.
 * For existing items, only the fields specified in `field_mask` are updated.
 */
export function bulkUpsertInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpsertInventoryItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'inventoryItems.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'inventoryItems.inventoryItem.createdDate' },
          { path: 'inventoryItems.inventoryItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkUpsertInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/upsert',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpsertInventoryItems;
}

/**
 * Updates multiple inventory items, given the provided filter.
 *
 * Each time an inventory item is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating an inventory item.
 * This ensures you're working with the latest inventory item and prevents unintended overwrites.
 */
export function bulkUpdateInventoryItemsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateInventoryItemsByFilter({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'inventoryItem.createdDate' },
          { path: 'inventoryItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkUpdateInventoryItemsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/update-by-filter',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __bulkUpdateInventoryItemsByFilter;
}

/** Deletes an inventory item. */
export function deleteInventoryItem(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteInventoryItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'DELETE' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.DeleteInventoryItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/inventory-items/{inventoryItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteInventoryItem;
}

/** Deletes multiple inventory items. */
export function bulkDeleteInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteInventoryItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDeleteInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteInventoryItems;
}

/**
 * Deletes multiple inventory items, given the provided filter.
 *
 * The operation runs asynchronously. Use the returned job ID to track progress.
 */
export function bulkDeleteInventoryItemsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteInventoryItemsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDeleteInventoryItemsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/delete-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteInventoryItemsByFilter;
}

/**
 * Retrieves a list of up to 1,000 inventory items, given the provided filtering, sorting, and cursor paging.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
 */
export function queryInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __queryInventoryItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'GET' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.QueryInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/inventory-items/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'inventoryItems.createdDate' },
              { path: 'inventoryItems.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
            protoPath: '/v3/inventory-items/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryInventoryItems;
}

/**
 * Retrieves a list of inventory items, given the provided filtering, sorting, and cursor paging.
 *
 *
 * Search Inventory Items runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * To learn about working with _Search_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 *
 * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
 */
export function searchInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __searchInventoryItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'GET' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.SearchInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/inventory-items/search',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'inventoryItems.createdDate' },
              { path: 'inventoryItems.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
            protoPath: '/v3/inventory-items/search',
            data: serializedData,
            host,
          }),
          data: serializedData,
        },
      ],
    };

    return metadata;
  }

  return __searchInventoryItems;
}

/**
 * Decrements quantities of multiple inventory items.
 *
 * > **Notes:**:
 * > + `trackQuantity` must be `true` to allow for decrementing the quantity.
 * > + If you pass `restrictInventory: true` and the `decrementData.decrementBy` amount is greater than the current quantity in stock, the request will fail with an `INSUFFICIENT_INVENTORY` error.
 * > + Pass `restrictInventory: false` to allow for negative quantities.
 * > + If you pass `preorderRequest: true` and the item is available for preorder, the item's `preorderCounter` will increase and the item's quantity will stay the same.
 */
export function bulkDecrementInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDecrementInventoryItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDecrementInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/decrement',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkDecrementInventoryItems;
}

/**
 * Increments quantities of multiple inventory items.
 *
 * > **Note:** `trackQuantity` must be `true` to allow for incrementing the quantity.
 */
export function bulkIncrementInventoryItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkIncrementInventoryItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkIncrementInventoryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/increment',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkIncrementInventoryItems;
}

/**
 * Decrements quantities of multiple inventory items by variant and location.
 *
 * > **Notes:**:
 * > + `trackQuantity` must be `true` to allow for decrementing the quantity.
 * > + If you pass `restrictInventory: true` and the `decrementData.decrementBy` amount is greater than the current quantity in stock, the request will fail with an `INSUFFICIENT_INVENTORY` error.
 * > + Pass `restrictInventory: false` to allow for negative quantities.
 * > + If you pass `preorderRequest: true` and the item is available for preorder, the item's `preorderCounter` will increase and the item's quantity will stay the same.
 */
export function bulkDecrementInventoryItemsByVariantAndLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDecrementInventoryItemsByVariantAndLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDecrementInventoryItemsByVariantAndLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/decrement-by-variant-and-location',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkDecrementInventoryItemsByVariantAndLocation;
}

/**
 * Increments quantities of multiple inventory items by variant and location.
 *
 * > **Note:** `trackQuantity` must be `true` to allow for incrementing the quantity.
 */
export function bulkIncrementInventoryItemsByVariantAndLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkIncrementInventoryItemsByVariantAndLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkIncrementInventoryItemsByVariantAndLocation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/increment-by-variant-and-location',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkIncrementInventoryItemsByVariantAndLocation;
}

/**
 * Set or Adjust inventory items which satisfy the provided filter.
 * If item track quantity, adjust it's quantity,
 * In case non-tracking, updates it to track and sets quantity to `incrementBy` or `decrementBy`.
 * @deprecated It has been replaced with com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkAdjustInventoryItemsByFilter().
 */
export function bulkSetOrAdjustInventoryItemsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkSetOrAdjustInventoryItemsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkSetOrAdjustInventoryItemsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/set-or-adjust-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkSetOrAdjustInventoryItemsByFilter;
}

/**
 * Adjust quantity of inventory items which satisfy the provided filter.
 * For items that track quantity: Adjusts the quantity based on incrementBy or decrementBy.
 * For items that do not track quantity:
 * if track_quantity is false (default), found items that are non-tracking will fail.
 * if track_quantity is true, non-tracking items will be updated to track quantity and quantity will be set to the provided amount.
 */
export function bulkAdjustInventoryItemsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAdjustInventoryItemsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkAdjustInventoryItemsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/adjust-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkAdjustInventoryItemsByFilter;
}

/**
 * Creates inventory items at a target location for variants matching a filter.
 *
 * Uses variants reader to filter items by criteria (e.g. categories) and creates
 * inventory items at the specified location for each matching variant.
 *
 * The operation runs asynchronously. Use the returned job ID to track progress.
 */
export function bulkCreateInventoryItemsByVariantFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateInventoryItemsByVariantFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.inventory_item',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkCreateInventoryItemsByVariantFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogInventoryApiV3InventoryServiceUrl({
        protoPath: '/v3/bulk/inventory-items/create-by-variant-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkCreateInventoryItemsByVariantFilter;
}
